import { useGesture } from '@use-gesture/react'
import { GroupProps, ThreeEvent } from '@react-three/fiber'
import { forwardRef, useRef } from 'react'
import { Group, Plane, Vector3 } from 'three'
import { handleClick } from '~/components/DrawingCanvas/util/eventHandlers'

export type DragControlsProps = {
  onTap?(event: ThreeEvent<MouseEvent>): void
  onDragStart?(event: ThreeEvent<PointerEvent>): void
  onDrag?(event: ThreeEvent<PointerEvent>, delta: Vector3, eventDelta: Vector3): void
  onDragEnd?(event: ThreeEvent<PointerEvent>): void
  onPointerOver?(event: ThreeEvent<PointerEvent>): void
  onPointerOut?(event: ThreeEvent<PointerEvent>): void
  dragDisabled?: boolean
} & GroupProps

const point2 = /* @__PURE__ */ new Vector3()
const delta = /* @__PURE__ */ new Vector3()
const eventDelta = /* @__PURE__ */ new Vector3()

export const DragControls = forwardRef<Group, DragControlsProps>(function DragControls(
  { children, onTap, onDragStart, onDrag, onDragEnd, onPointerOver, onPointerOut, dragDisabled, ...props },
  ref
) {
  const plane = new Plane()
  const UP = new Vector3(0, 0, 1)
  const point1 = useRef<Vector3>(new Vector3())
  const previousPoint = useRef<Vector3>(new Vector3())

  const bind = useGesture(
    {
      onDragStart({ event: event_, intentional }) {
        const event = event_ as unknown as ThreeEvent<PointerEvent>
        if (dragDisabled) return

        event.stopPropagation()

        if (!intentional) return

        onDragStart?.(event)

        const point = event.intersections[0]?.point
        if (point) {
          point1.current.copy(point)
          previousPoint.current.copy(point)
        }
      },
      onDrag({ event: event_, tap, intentional }) {
        const event = event_ as unknown as ThreeEvent<PointerEvent>

        if (tap || dragDisabled) return

        event.stopPropagation()

        if (!intentional) return

        plane.setFromNormalAndCoplanarPoint(UP, point1.current)
        const point = event.ray.intersectPlane(plane, new Vector3())
        if (point) {
          point2.copy(point)
          delta.subVectors(point2, point1.current)
        }

        eventDelta.subVectors(point2, previousPoint.current)

        onDrag?.(event, delta, eventDelta)
        previousPoint.current.copy(point2)
      },
      onDragEnd({ event: event_, intentional }) {
        const event = event_ as unknown as ThreeEvent<PointerEvent>

        if (dragDisabled) return

        event.stopPropagation()

        if (!intentional) return

        onDragEnd?.(event)
      },
      onPointerOver({ event: event_ }) {
        const event = event_ as unknown as ThreeEvent<PointerEvent>

        onPointerOver?.(event)
      },
      onPointerOut({ event: event_ }) {
        const event = event_ as unknown as ThreeEvent<PointerEvent>

        onPointerOut?.(event)
      },
    },
    {
      drag: {
        filterTaps: true,
        threshold: 1,
        triggerAllEvents: true,
      },
    }
  )

  return (
    // @ts-expect-error slightly differing event types, this is done by drei so it should be fine for us
    <group {...props} {...bind()} onClick={onTap ? handleClick(onTap) : undefined} ref={ref}>
      {children}
    </group>
  )
})
